import CustomSelect from "../form/CustomSelect";
import { useMemo } from "react";

const rowPerPageStyle = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "0px",
    height: "auto",
    width: "120px", // slightly wider to fit placeholder/input
    borderColor: state.isFocused || state.selectProps.menuIsOpen ? "var(--primary, #7367f0)" : "#E4E6EF",
    boxShadow: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    padding: "0 8px",
    backgroundColor: "var(--white, #ffffff)",
    "&:hover": {
      borderColor: state.isFocused || state.selectProps.menuIsOpen ? "var(--primary, #7367f0)" : "#d8d6de",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0",
    height: "auto",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  }),
  singleValue: (provided) => ({
    ...provided,
    margin: "0",
    color: "#5e5873",
    fontWeight: "600",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0",
    padding: "0",
    color: "#5e5873",
    fontWeight: "600",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "30px",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    marginRight: "4px",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "0 6px 0 0",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    boxShadow: "0 4px 24px 0 rgba(34, 41, 47, 0.1)",
    border: "1px solid #E4E6EF",
    padding: "2px 6px",
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "var(--primary, #7367f0)"
      : state.isFocused
        ? "rgba(115, 103, 240, 0.08)"
        : "transparent",
    color: state.isSelected ? "#ffffff" : "#5e5873",
    cursor: "pointer",
    padding: "4px 8px",
    fontSize: "13px",
    fontWeight: "500",
    borderRadius: "4px",
    marginBottom: "4px",
    textAlign: "center",
    "&:active": {
      backgroundColor: "var(--primary, #7367f0)",
      color: "#ffffff",
    },
    "&:last-child": {
      marginBottom: "0"
    },
  }),
};

const RowsPerPageSelect = ({
  pageSize,
  setPageSize,
  setCurrentPage,
  perPageOptions = [],
  loading = false,
}) => {
  // Build and sort options, merging active custom sizes if created
  const options = useMemo(() => {
    const baseOptions = perPageOptions.map((v) => ({ value: Number(v), label: String(v) }));
    const numericSize = Number(pageSize);
    if (numericSize && !perPageOptions.includes(numericSize)) {
      baseOptions.push({ value: numericSize, label: String(numericSize) });
      baseOptions.sort((a, b) => a.value - b.value);
    }
    return baseOptions;
  }, [perPageOptions, pageSize]);

  // Keep the SAME object reference for the value
  const value = useMemo(() => {
    const numericSize = Number(pageSize);
    const existing = options.find((o) => o.value === numericSize);
    if (existing) return existing;
    return { value: numericSize, label: String(numericSize) };
  }, [options, pageSize]);

  return (
    <CustomSelect
      label={{ isHidden: true }}
      isCreatable={true}
      instanceId="rows-per-page" // stable id -> avoids SSR id drift
      isSearchable={true}
      isDisabled={loading}
      isClearable={false}
      value={value}
      onChange={(opt) => {
        if (!opt) return;
        const val = Number(opt.value);
        if (isNaN(val) || val <= 0) return;
        setPageSize(val);
        setCurrentPage(1);
      }}
      options={options}
      classNamePrefix="row-per"
      menuPlacement="auto"
      placeholder="Select or create"
      styles={rowPerPageStyle}
      menuPortalTarget={document.body}
      isValidNewOption={(inputValue) => {
        const val = Number(inputValue);
        return !isNaN(val) && val > 0 && Number.isInteger(val);
      }}
      // menuIsOpen={true}
      formatCreateLabel={(inputValue) => `Show ${inputValue}`}
      classNames={{
        control: () => "",
        menu: () => "",
        menuList: () => "",
        option: () => "",
        valueContainer: () => "",
        singleValue: () => "",
        input: () => "",
        indicatorsContainer: () => "",
        dropdownIndicator: () => "",
        indicatorSeparator: () => "",
      }}
    />
  );
};

export default RowsPerPageSelect;
